import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  ColDef,
  FirstDataRenderedEvent,
  GridApi,
  GridReadyEvent,
} from '@ag-grid-community/core';
import { Logger } from '@utils/logger';
import { FilterType, OptionWithCount } from '@types';
import { DropdownItem, ResponsiveService } from '@frk/eds-components';
import {
  ApiParameters,
  GridHeader,
  GridRowData,
  ProductsUserData,
  ProductType,
  ProductTypesData,
  Records,
  RowDataChange,
  SelectAllDocs,
  SubscribedProductDTO,
  SubscriptionProductsData,
  SwitchState,
} from '@marketing/subscriptions-manage/subscription-manage.interface';
import { NameCellRendererComponent } from '@marketing/subscriptions-manage/name-cell-renderer/name-cell-renderer.component';
import { CheckboxCellRendererComponent } from '@marketing/subscriptions-manage/checkbox-cell-renderer/checkbox-cell-renderer.component';
import { DocumentsCellRendererComponent } from '@marketing/subscriptions-manage/documents-cell-renderer/documents-cell-renderer.component';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { ProductTypeParameter } from '@products/services/graphql-fund-data.service';
import { BaseFilter } from '@products/fund-listing/services/fund-filter.service';
import { SubscriptionsService } from '@marketing/subscriptions-manage/services/subscriptions.service';
import { SubscriptionProductsService } from '@marketing/subscriptions-manage/services/subscription-products.service';
import { NgForm } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';

/**
 * Logger
 */
const logger = Logger.getLogger('SubscriptionsManageComponent');

type DropDownFilterType = FilterType & {
  preSelectedValues:
    | OptionWithCount
    | OptionWithCount[]
    | DropdownItem
    | DropdownItem[];
};

@Component({
  selector: 'ft-subscriptions-products-ii',
  templateUrl: './subscriptions-products-ii.component.html',
  styleUrls: ['./subscriptions-products-ii.component.scss'],
})
export class SubscriptionsProductsIiComponent implements OnInit, OnDestroy {
  @ViewChild('subscriptionForm') ngForm: NgForm;
  @Input() apiUrl!: string;
  private productData: SubscriptionProductsData;
  private productTypesData: ProductTypesData;
  private productsTypeData$: Observable<ProductTypesData>;
  private productsUserData$: Observable<ProductsUserData>;
  private filter01: DropDownFilterType = {
    label: 'product-type',
    preSelectedValues: {
      value: ProductTypeParameter.MUTUAL_FUNDS,
      label: 'Mutual Funds',
    },
    type: new BaseFilter('PRODUCTS', () => true, []),
    options: [],
  };
  private gridApi: GridApi;
  private docTypesHeaders: GridHeader[];
  private unsubscribe$: Subject<void> = new Subject<void>();
  private searchString$ = new Subject<string>();
  private searchString: string;
  private userData: ProductsUserData;
  private selectedDocuments = [];
  private rowHeight: number;
  private isMobile$: Observable<boolean>;

  public filterTypes: DropDownFilterType[];
  public records: Records = {
    showed: 0,
    all: 0,
  };
  public rowData!: GridRowData[];
  public columnDefs: ColDef[];
  public defaultColDef: ColDef;
  public frameworkComponents: {
    nameCellRendererComponent: typeof NameCellRendererComponent;
    checkboxCellRendererComponent: typeof CheckboxCellRendererComponent;
    documentsCellRendererComponent: typeof DocumentsCellRendererComponent;
  };
  public subscriptionOnlyState: SwitchState = SwitchState.OFF;
  public textFilterValue: string;
  public resetFilterButton = false;
  public selectedItemsCount = 0;
  public hasSubscriptions = false;
  public isLoading = true;
  public globalUnsubscribe = false;
  public loggedIn: boolean;
  public isDisabledSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    true
  );

  constructor(
    private subscriptionsService: SubscriptionsService,
    private subscriptionsProductsService: SubscriptionProductsService,
    private route: ActivatedRoute,
    private spinner: NgxSpinnerService,
    private changeDetector: ChangeDetectorRef,
    private responsiveService: ResponsiveService
  ) {
    // TODO: Temporarily added mock data. To remove when API will be fixed.
    this.productTypesData = require('./__fixtures/subscribableProductTypeDocuments.json');
    this.filterTypes = [this.filter01];
    this.frameworkComponents = {
      nameCellRendererComponent: NameCellRendererComponent,
      checkboxCellRendererComponent: CheckboxCellRendererComponent,
      documentsCellRendererComponent: DocumentsCellRendererComponent,
    };
    this.isMobile$ = this.responsiveService.isMobile$();
  }

  ngOnInit(): void {
    this.spinner
      .show('countLoadingSpinner')
      .then(() => logger.debug('Count Spinner set'));
    let globalId = null;
    this.productsUserData$ = this.route.queryParams.pipe(
      takeUntil(this.unsubscribe$),
      switchMap((params: { globalId: number }) => {
        globalId = params.globalId;
        if (!globalId) {
          // Set loggedIn to false when globalId is not set. This is required for MSD integration,
          // where globalID will be set after Okta verification.
          this.loggedIn = false;
          return;
        }
        this.loggedIn = true;
        return this.subscriptionsService.getApiGetRequest$(
          this.apiUrl + 'productSubscriptions/globalId/' + globalId
        );
      })
    );

    this.productsUserData$.pipe(takeUntil(this.unsubscribe$)).subscribe(
      (userData: ProductsUserData) => {
        this.subscriptionsProductsService.setUserData(userData);
        this.userData = this.subscriptionsProductsService.getUserData();
        this.globalUnsubscribe = this.userData.globalUnsubscribe;

        if (!this.globalUnsubscribe) {
          const apiParameters: ApiParameters = {};
          apiParameters.region = this.userData.region;
          apiParameters.investorType = this.userData.investorType;
          this.productsTypeData$ = this.subscriptionsService.getApiGetRequest$(
            this.apiUrl + 'subscribableProductTypeDocuments',
            apiParameters
          );
          this.defaultColDef = this.subscriptionsProductsService.getDefaultColDefs();

          this.productsTypeData$.pipe(takeUntil(this.unsubscribe$)).subscribe(
            (productsTypeData: ProductTypesData) => {
              if (productsTypeData.productTypes.length > 0) {
                this.productTypesData = productsTypeData;
              }
              this.columnDefs = this.getColDefs();
              logger.debug(this.columnDefs);
              this.setProductTypes(this.productTypesData);
              this.getColData();
            },
            (error) => {
              logger.debug('Products data load error', error);
              this.rowData = null;
              this.setLoadingEnd();
            }
          );
        } else {
          this.setLoadingEnd();
        }
      },
      (error) => {
        logger.debug('User data load error', error);
        this.rowData = null;
        this.setLoadingEnd();
      }
    );

    this.subscriptionsProductsService
      .getRowDataChange$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((rowDataChange: RowDataChange) => {
        const changedKeys = rowDataChange.changedDocuments;
        logger.debug(changedKeys);
        rowDataChange.changedDocuments.forEach((selection: string) => {
          this.selectionCount(selection);
        });
        this.gridApi.refreshCells({ force: true });
      });

    //  Filtering observables.
    this.searchString$.pipe(takeUntil(this.unsubscribe$)).subscribe((value) => {
      this.textFilterValue = this.searchString = value;
      this.resetFilterButton = this.resetButtonStatus();
      this.filterChangeUpdater();
    });
    logger.debug('Component initialised');
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  // ###############
  // PRIVATE METHODS
  // ###############
  // Ag-Grid setup
  /**
   * Get column definition for table
   */
  private getColDefs(
    docType: ProductTypeParameter | string = this.productTypesData
      .productTypes[0].typeId
  ): ColDef[] {
    const colDefs: ColDef[] = [
      {
        colId: 'ID',
        field: 'ID',
        hide: true,
      },
      {
        colId: 'ASSET_CLASS',
        field: 'ASSET_CLASS',
        hide: true,
      },
      {
        colId: 'NAME',
        field: 'NAME',
        headerName: 'Fund Name',
        cellClass: ['ag-cell-frk--highlightedPrimary', 'ag-cell-frk--middle'],
        cellRenderer: 'nameCellRendererComponent',
        cellRendererParams: {
          context: {
            selectAllDocs: this.selectAllDocs,
            checkSelectedAll: this.subscriptionsProductsService
              .checkSelectedAll,
            docTypesHeaders: this.subscriptionsProductsService.getDocumentTypes,
            productsV2: true,
          },
        },
        minWidth: 200,
        maxWidth: 2000,
        sortingOrder: ['asc', 'desc'],
        sort: 'asc',
        flex: 2,
        cellStyle: { 'font-weight': 'bold' },
      },
      {
        colId: 'DOCUMENTS',
        field: 'DOCUMENTS',
        headerName: 'Documents',
        cellClass: ['ag-cell-frk--middle'],
        cellRenderer: 'documentsCellRendererComponent',
        cellRendererParams: {
          context: {
            docTypesHeaders: this.subscriptionsProductsService.getDocumentTypes,
          },
        },
        minWidth: 230,
        maxWidth: 2000,

        flex: 2,
      },
    ];
    const selectedProductType: ProductType = this.productTypesData.productTypes.find(
      (node: ProductType) => {
        return node.typeId === docType;
      }
    );
    if (selectedProductType?.documentTypes) {
      this.subscriptionsProductsService.setDocumentTypes(
        selectedProductType.documentTypes
      );
      this.docTypesHeaders = this.subscriptionsProductsService.getDocumentTypes();
    }
    return colDefs;
  }

  /**
   * Setting data for AG-Grid
   */
  private getColData(
    productType: ProductTypeParameter | string = this.productTypesData
      .productTypes[0].typeId
  ): void {
    this.isLoading = true;
    const apiParameters: ApiParameters = {};
    apiParameters.region = this.userData.region;
    apiParameters.investorType = this.userData.investorType;
    if (productType !== '') {
      apiParameters.typeId = productType;
    }
    this.subscriptionsService
      .getApiGetRequest$(
        this.apiUrl + 'subscribableProductTypeProducts',
        apiParameters
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (productData: SubscriptionProductsData) => {
          logger.debug(productData);
          this.productData = productData;
          this.subscriptionsProductsService.setRowData(productData);
          this.rowData = this.subscriptionsProductsService.getRowData();
          this.records.all = this.records.showed = this.rowData.length;
          this.hasSubscriptions = this.getHasSubscriptions();
          this.setLoadingEnd();
        },
        (error) => {
          logger.debug('Data loading error:', error);
          this.setLoadingEnd();
          this.gridApi.showNoRowsOverlay();
        }
      );
  }

  // Filtering methods
  /**
   * Set Product Type Options
   * @param productTypesData - ProductTypesData
   */
  private setProductTypes(productTypesData: ProductTypesData): void {
    this.filter01.options = productTypesData.productTypes.map(
      (productType: ProductType) => {
        return { value: productType.typeId, label: productType.typeLabel };
      }
    );
    this.filter01.preSelectedValues = this.filter01.options[0];
  }

  /**
   * Updating filtering and showed records #
   */
  private filterChangeUpdater(): void {
    this.gridApi.onFilterChanged();
    this.records.showed = this.gridApi.getDisplayedRowCount();
  }

  /**
   * Get SearchBox Filter
   * @param data - GridRowData
   */
  private getSearchBoxFilter(data: GridRowData): boolean {
    return this.searchString?.length > 0
      ? data.NAME.toLowerCase().includes(this.searchString.toLowerCase()) ||
          data.ID.includes(this.searchString)
      : true;
  }

  /**
   * Reset filter status
   */
  private resetButtonStatus = (): boolean => {
    return (
      this.subscriptionOnlyState === SwitchState.ON || !!this.textFilterValue
    );
  };

  /**
   * Set all doc types to selected
   * @param cellParams - SelectAllDocs
   */
  private selectAllDocs = (cellParams: SelectAllDocs): void => {
    const rowNode = this.gridApi.getRowNode(cellParams.dataParams.ID);
    logger.debug('Updating node');
    const newData: GridRowData = {
      ID: cellParams.dataParams.ID,
      PRODUCT_ID: cellParams.dataParams.PRODUCT_ID,
      ASSET_CLASS: cellParams.dataParams.ASSET_CLASS,
      NAME: cellParams.dataParams.NAME,
    };
    const selectedAll = this.subscriptionsProductsService.checkSelectedAll(
      cellParams.dataParams,
      this.docTypesHeaders
    );
    this.docTypesHeaders.forEach((docTypeHeader: GridHeader) => {
      newData[docTypeHeader.id] = !selectedAll;
      if (cellParams.dataParams[docTypeHeader.id] !== !selectedAll) {
        this.selectionCount(`${cellParams.dataParams.ID}-${docTypeHeader.id}`);
      }
    });
    rowNode.setData(newData);
    // Updating Row Data
    this.rowData[this.rowData.indexOf(cellParams.dataParams)] = newData;
    logger.debug('Data updated');
  };

  /**
   * Counting selected documents
   * @param selectedDocument - true to not check if user has any active/inactive subscription.
   */
  private selectionCount = (selectedDocument?: string): void => {
    if (selectedDocument) {
      const alreadySelected = this.selectedDocuments.some((doc) => {
        return doc === selectedDocument;
      });
      if (alreadySelected) {
        this.selectedDocuments.splice(
          this.selectedDocuments.indexOf(selectedDocument),
          1
        );
      } else {
        this.selectedDocuments.push(selectedDocument);
      }
    }
    this.hasSubscriptions = this.getHasSubscriptions();
    this.selectedItemsCount = this.selectedDocuments.length;
    this.isDisabledSubject$.next(this.selectedItemsCount === 0);
    this.gridApi.refreshCells({ force: true });
  };

  /**
   * Set Loading Data End
   */
  private setLoadingEnd = (): void => {
    this.isLoading = false;
    this.selectedItemsCount = 0;
    this.selectedDocuments = [];
    this.isDisabledSubject$.next(true);
    this.changeDetector.detectChanges();
  };

  /**
   * Check User Subscriptions
   */
  private getHasSubscriptions(): boolean {
    return this.rowData.some((row: GridRowData) => {
      return row.HAS_SUBSCRIPTION;
    });
  }

  // ##############
  // PUBLIC METHODS
  // ##############
  /**
   * Reset filters
   */
  public resetFilters($event?: MouseEvent) {
    this.searchString = null;
    this.subscriptionsProductsService.setSubscriptionsOnly(false);
    this.subscriptionOnlyState = SwitchState.OFF;
    this.textFilterValue = '';
    this.filterChangeUpdater();
    this.resetFilterButton = false;
    if ($event) {
      $event.preventDefault();
    }
  }

  /**
   * Search filter
   * @param $event - search string
   */
  public searchByKeyword($event: string): void {
    logger.debug('Search for:', $event);
    this.searchString$.next($event);
  }

  /**
   * Update external filters
   * @param label - filter label string
   * @param $event - filter DropdownItem event
   */
  public updateFilter(
    label: string,
    $event: DropdownItem | DropdownItem[]
  ): void {
    logger.debug('Updated filter', label, $event);
    if (label === 'product-type') {
      const event = $event as DropdownItem;
      this.filter01.preSelectedValues = event;
      this.columnDefs = this.getColDefs(event.value);
      this.getColData(event.value);
      // Reset filter after product type change.
      this.selectedItemsCount = 0;
      this.resetFilters();
    }
  }

  /**
   * Get Row Node ID from data
   * @param data - GridRowData
   */
  public getRowNodeId = (data: GridRowData): string => {
    return data.ID;
  };

  /**
   * Set using external filter
   */
  public isExternalFilterPresent = (): boolean => true;

  /**
   * Checks external filter
   * @param node - row node
   */
  public doesExternalFilterPass = (node: any): boolean => {
    return (
      this.getSearchBoxFilter(node.data) &&
      this.subscriptionsProductsService.getSubscriptionOnlyFilter(node.data)
    );
  };

  /**
   * Execute during grid first load
   * @param $event - FirstDataRenderedEvent
   */
  public onFirstDataRendered($event: FirstDataRenderedEvent): void {
    logger.debug('First render:', $event);
  }

  /**
   * Execute when Grid ready
   * @param $event - GridReadyEvent
   */
  public onGridReady($event: GridReadyEvent): void {
    this.gridApi = $event.api;
    this.gridApi.sizeColumnsToFit();
    this.isMobile$.pipe(takeUntil(this.unsubscribe$)).subscribe((isMobile) => {
      this.rowHeight = isMobile ? 120 : 60;
      this.defaultColDef = this.subscriptionsProductsService.getDefaultColDefs();
      this.gridApi.resetRowHeights();
    });
    logger.debug('Grid Ready', $event);
  }

  /**
   * Returns Row Height
   */
  public getRowHeight = (): number => this.rowHeight;

  /**
   * Subscriptions only filter on/off
   * @param $event - toggle selection string event
   */
  public toggleSubscriptionsOnly($event: string): void {
    logger.debug($event);
    this.subscriptionsProductsService.setSubscriptionsOnly($event === 'on');
    this.subscriptionOnlyState =
      $event === 'on' ? SwitchState.ON : SwitchState.OFF;
    this.resetFilterButton = this.resetButtonStatus();
    this.filterChangeUpdater();
  }

  /**
   * Save subscribed data.
   */
  public submitSubscriptions(): void {
    logger.debug('Submit Form');
    // Filter submitted products
    const submittedProducts: GridRowData[] = this.subscriptionsProductsService.getSubmittedProducts();
    const submittedDocs: SubscribedProductDTO[] = this.subscriptionsProductsService.getSubmittedDocs(
      submittedProducts
    );
    // Saving data in MSD
    this.isLoading = true;
    this.subscriptionsService
      .getApiPutRequest$(
        this.apiUrl + 'productSubscriptions/globalId/' + this.userData.globalId,
        submittedDocs
      )
      .pipe(
        takeUntil(this.unsubscribe$),
        switchMap(() => this.productsUserData$)
      )
      .subscribe(
        (userData) => {
          this.userData = userData;
          this.setLoadingEnd();
          logger.debug('Products saved');
        },
        (error) => {
          this.setLoadingEnd();
          logger.debug('Products save error', error);
        }
      );
  }

  emailPreferences($event: MouseEvent) {
    logger.debug('Email Preferences', $event);
    this.subscriptionsProductsService.setIsProductsModalVisible$(true);
    this.subscriptionsProductsService.setIsEmailPreferences$(true);
  }
}
